import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography } from '@mui/material';
import { labelText } from "../common/sharedFunctions";
import localization from '../components/Table/Localization';
import { getModuleActionPermissions, filterEditableActions } from "common/src/actions/userPermissions";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

export default function AdditionalFees() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const label = (key, fallback) => {
    return labelText(t, key, fallback);
  };
  const {
    fetchAdditionalFees,
    addAdditionalFee,
    updateAdditionalFee,
    deleteAdditionalFee
  } = api;
  const settings = useSelector(state => state.settingsdata.settings);
  const additionalfeesdata = useSelector(state => state.additionalfees);
  const [data, setData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const dispatch = useDispatch();    const auth = useSelector((state) => state.auth);

    const additionalFeesPermissions = getModuleActionPermissions({
        role: auth && auth.profile && auth.profile.usertype,
        moduleKey: "additionalFeesTitle",
        accessPermissions: settings && settings.accessPermissions
    });

  useEffect(() => {
    dispatch(fetchAdditionalFees());
  }, [dispatch, fetchAdditionalFees]);

  useEffect(() => {
    setData(additionalfeesdata.fees || []);
  }, [additionalfeesdata.fees]);

  const columns = [
    { title: label('fee_name', 'Name of Fee'), field: 'name', render: rowData => <span>{rowData.name}</span> },
    {
      title: label('fee_type', 'Percentage or Flat Rate'),
      field: 'rateType',
      lookup: { flat: label('flat_rate', 'Flat Rate'), percentage: label('percentage', 'Percentage') },
      initialEditValue: 'flat'
    },
    { title: label('amount', 'Amount'), field: 'amount', type: 'numeric', initialEditValue: 0 },
    {
      title: label('active_status', 'Active Status'),
      field: 'active',
      type: 'boolean',
      initialEditValue: true,
      render: rowData => <AdminStatusBadge status={rowData.active === false ? "INACTIVE" : "ACTIVE"}>{rowData.active === false ? label('false', 'false') : label('true', 'true')}</AdminStatusBadge>
    }
  ];

  const activeFees = data.filter((fee) => fee.active !== false).length;
  const flatFees = data.filter((fee) => fee.rateType === "flat").length;
  const percentageFees = data.filter((fee) => fee.rateType === "percentage").length;

  return (
    additionalfeesdata.loading ? <CircularLoading /> :
      <Box className="garahex-finance-additional-fees-page" sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
        <PageHeader
          eyebrow={label("finance", "Finance")}
          title={label('additional_fees_title', 'Additional Fees')}
          description={label("additional_fees_admin_description", "Manage the existing additional-fee records used by the booking workflow without changing fee calculations or save behavior.")}
          actions={<StatusBadge tone="info">{`${data.length} ${label("records", "records")}`}</StatusBadge>}
        />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: label("total_fees", "Total Fees"), value: data.length, color: adminTokens.colors.text },
            { label: label("active_status", "Active"), value: activeFees, color: adminTokens.colors.success },
            { label: label("flat_rate", "Flat Rate"), value: flatFees, color: adminTokens.colors.garahexOrange },
            { label: label("percentage", "Percentage"), value: percentageFees, color: adminTokens.colors.purple }
          ].map((item) => (
            <Grid item xs={12} sm={6} lg={3} key={item.label}>
              <SurfaceCard sx={{ minHeight: 112 }}>
                <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{item.label}</Typography>
                <Typography sx={{ color: item.color, fontSize: "1.9rem", fontWeight: 950, mt: 0.8 }}>{item.value}</Typography>
              </SurfaceCard>
            </Grid>
          ))}
        </Grid>
        <SurfaceCard>
          <SectionHeader
            title={label('additional_fees_title', 'Additional Fees')}
            description={label("additional_fees_table_description", "Inline add, edit, delete, validation, export, search, and permission behavior are preserved.")}
          />
          <AdminDataTable
            title={label('additional_fees_title', 'Additional Fees')}
            columns={columns}
            style={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}
            data={data}
            emptyTitle={label("no_additional_fees_found", "No additional fees found")}
            emptyDescription={label("no_additional_fees_found_description", "No additional fee records are available in the current finance scope.")}
            onRowClick={((evt, selectedRow) => setSelectedRow(selectedRow.tableData.id))}
            options={{
              exportButton: true,
              rowStyle: (rowData) => ({
                backgroundColor:
                  selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" : "#FFFFFF"
              }),
            }}
            localization={localization(t)}
            editable={settings.AllowCriticalEditsAdmin ? filterEditableActions({ editable: {
            onRowAdd: newData =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  if (!(newData && newData.name)) {
                    alert(label('no_details_error', 'Please fill up all the details properly.'));
                    reject();
                  } else {
                    dispatch(addAdditionalFee(newData)).then(resolve).catch((error) => {
                      alert(error && error.message ? error.message : label('no_details_error', 'Please fill up all the details properly.'));
                      reject();
                    });
                  }
                }, 600);
              }),
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  if (!(newData && newData.name)) {
                    alert(label('no_details_error', 'Please fill up all the details properly.'));
                    reject();
                  } else if (newData !== oldData) {
                    dispatch(updateAdditionalFee(newData)).then(resolve).catch((error) => {
                      alert(error && error.message ? error.message : label('no_details_error', 'Please fill up all the details properly.'));
                      reject();
                    });
                  } else {
                    resolve();
                  }
                }, 600);
              }),
            onRowDelete: oldData =>
              new Promise((resolve, reject) => {
                setTimeout(() => {
                  dispatch(deleteAdditionalFee(oldData)).then(resolve).catch((error) => {
                    alert(error && error.message ? error.message : label('no_details_error', 'Please fill up all the details properly.'));
                    reject();
                  });
                }, 600);
              }),
          }, permissions: additionalFeesPermissions }) : null}
          />
        </SurfaceCard>
      </Box>
  );
}
